'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { authApi } from '@/services/api/authApi';
import { userApi } from '@/services/api/userApi';
import {
  beginIntentionalLogout,
  cancelIntentionalLogout,
  type ParsedApiError,
} from '@/services/api/baseApi';
import {
  AUTH_NOTICE_PASSWORD_CHANGED,
  broadcastNoticeCleared,
  clearAuthData,
  clearAuthNotice,
  clearLegacyAuthTokens,
  setAuthNotice,
  subscribeAuthCleared,
  subscribeNoticeCleared,
} from '@/services/api/logoutUtil';
import { handleApiError } from '@/services/api/types';
import toast from 'react-hot-toast';

const MOCK_MODE = process.env.NEXT_PUBLIC_MOCK_MODE === 'true';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  school?: string;
  subject?: string;
  grade_level?: string;
  section?: string;
  has_accepted_terms_and_conditions?: boolean;
  datetime_last_accepted_terms_and_conditions?: string;
  features: string[];
}

interface ApiError {
  message: string;
  response?: {
    data?: {
      detail?: string;
    };
  };
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

interface UserApiResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  school?: string;
  subject?: string;
  grade_level?: string;
  section?: string;
  has_accepted_terms_and_conditions?: boolean;
  datetime_last_accepted_terms_and_conditions?: string;
  features?: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  showTermsModal: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<{
    success: boolean;
    error?: string;
    fieldErrors?: Record<string, string[]>;
  }>;
  refreshUser: () => Promise<void>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{
    success: boolean;
    error?: string;
    field?: 'password';
  }>;
  endSessionAfterPasswordChange: () => void;
  acceptTermsAndConditions: () => Promise<{ success: boolean; error?: string }>;
  closeTermsModal: () => void;
  checkAndShowTermsModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function mapUser(typed: UserApiResponse): User {
  return {
    id: typed.id || '',
    email: typed.email || '',
    first_name: typed.first_name || '',
    last_name: typed.last_name || '',
    role: typed.role || '',
    school: typed.school || '',
    subject: typed.subject,
    grade_level: typed.grade_level,
    section: typed.section,
    has_accepted_terms_and_conditions: typed.has_accepted_terms_and_conditions,
    datetime_last_accepted_terms_and_conditions: typed.datetime_last_accepted_terms_and_conditions,
    features: typed.features ?? [],
  };
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const router = useRouter();
  const routerRef = useRef(router);
  const userRef = useRef<User | null>(null);

  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Clear any legacy JWTs left from the pre-cookie auth era (one-time on boot).
  useEffect(() => {
    clearLegacyAuthTokens();
  }, []);

  // Session expiry from baseApi interceptor (refresh failed after a 401).
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent) => {
      const { message } = event.detail;
      const hadUser = !!userRef.current;

      setUser(null);

      // Avoid toasting cold unauthenticated visits (login page boot).
      if (hadUser) {
        toast.error(message || 'Your session expired. Please login again.', {
          duration: 4000,
        });
        setTimeout(() => {
          router.push('/login');
        }, 500);
      }
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired as EventListener);
    return () => window.removeEventListener('auth:sessionExpired', handleSessionExpired as EventListener);
  }, [router]);

  // Sync logout across tabs via BroadcastChannel (cookies aren't visible to storage events).
  useEffect(() => {
    const unsubscribeLogout = subscribeAuthCleared((reason) => {
      if (reason === 'password_changed') {
        beginIntentionalLogout();
        setAuthNotice(AUTH_NOTICE_PASSWORD_CHANGED);
        toast.dismiss();
        userRef.current = null;
        setUser(null);
        routerRef.current.replace('/login');
        return;
      }

      if (!userRef.current) {
        return;
      }
      // No toast here — the initiating tab already notified; avoid double-firing in same tab.
      userRef.current = null;
      setUser(null);
      routerRef.current.replace('/login');
    });

    const unsubscribeNotice = subscribeNoticeCleared(() => {
      clearAuthNotice();
    });

    return () => {
      unsubscribeLogout();
      unsubscribeNotice();
    };
  }, []);

  /**
   * Logged in = GET /user/me/ returns 200.
   * On 401, BaseApi silently refreshes once (deduped) and retries.
   * If still unauthorized after that, treat as logged out.
   */
  const fetchUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (MOCK_MODE) {
        // Keep prior mock user if present; otherwise stay logged out until login().
        return;
      }

      try {
        const userData = await userApi.getUserInfo();
        if (userData && typeof userData === 'object') {
          setUser(mapUser(userData as unknown as UserApiResponse));
          // Shared cookies can authenticate this tab without login() — drop a stale banner.
          clearAuthNotice();
        } else {
          throw new Error('Invalid user data received');
        }
      } catch {
        setUser(null);
      }
    } catch (err: unknown) {
      const apiError = err as ApiError;
      setUser(null);
      setError(apiError.message || 'Failed to fetch user info');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    try {
      if (MOCK_MODE) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setUser({
          id: 'mock-1',
          email,
          first_name: 'Mock',
          last_name: 'User',
          role: 'Student',
          features: [],
          has_accepted_terms_and_conditions: true,
        });
        clearAuthNotice();
        broadcastNoticeCleared();
        cancelIntentionalLogout();
        return { success: true };
      }

      await authApi.signIn({ username: email, password });
      // Cookie session must be established — login 200 alone is not enough.
      const userData = await userApi.getUserInfo();
      if (!userData || typeof userData !== 'object') {
        return {
          success: false,
          error: 'Login succeeded but no session cookie was established. Please try again.',
        };
      }
      setUser(mapUser(userData as unknown as UserApiResponse));
      clearAuthNotice();
      broadcastNoticeCleared();
      cancelIntentionalLogout();
      return { success: true };
    } catch (error: unknown) {
      if (error instanceof Error) {
        if ('fieldErrors' in error) {
          const fieldErrors = (error as { fieldErrors?: Record<string, string> }).fieldErrors;
          if (fieldErrors && Object.keys(fieldErrors).length > 0) {
            const errorMessages = Object.entries(fieldErrors)
              .map(([field, msg]) => `${field}: ${msg}`)
              .join(', ');
            return { success: false, error: errorMessages };
          }
        }
        const errorMessage = error.message.toLowerCase();
        if (
          errorMessage.includes('401') ||
          errorMessage.includes('unauthorized') ||
          errorMessage.includes('invalid credentials') ||
          errorMessage.includes('authentication failed') ||
          errorMessage.includes('incorrect password') ||
          errorMessage.includes('user not found') ||
          errorMessage.includes('no user found') ||
          errorMessage.includes('no active account')
        ) {
          return {
            success: false,
            error: 'Invalid email or password. Please check your credentials and try again.',
          };
        }
        if (
          errorMessage.includes('404') ||
          errorMessage.includes('not found') ||
          errorMessage.includes('user does not exist')
        ) {
          return {
            success: false,
            error:
              'No account found with this email address. Please check your email or create a new account.',
          };
        }
        if (
          errorMessage.includes('500') ||
          errorMessage.includes('internal server error') ||
          errorMessage.includes('server error')
        ) {
          return {
            success: false,
            error: 'Server error occurred. Please try again in a few minutes.',
          };
        }
        if (
          errorMessage.includes('network error') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('connection failed') ||
          errorMessage.includes('fetch failed')
        ) {
          return {
            success: false,
            error: 'Connection failed. Please check your internet connection and try again.',
          };
        }
        if (
          errorMessage.includes('429') ||
          errorMessage.includes('too many requests') ||
          errorMessage.includes('rate limit')
        ) {
          return {
            success: false,
            error: 'Too many login attempts. Please wait a few minutes before trying again.',
          };
        }
        if (
          error.message &&
          !error.message.includes('something went wrong') &&
          !error.message.includes('login failed') &&
          error.message.length > 10
        ) {
          return { success: false, error: error.message };
        }
      }
      return {
        success: false,
        error: 'Invalid email or password. Please check your credentials and try again.',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    data: RegisterData
  ): Promise<{
    success: boolean;
    error?: string;
    fieldErrors?: Record<string, string[]>;
  }> => {
    setIsLoading(true);
    try {
      if (MOCK_MODE) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setUser({
          id: 'mock-1',
          email: data.email,
          first_name: data.firstName,
          last_name: data.lastName,
          role: 'Student',
          features: [],
          has_accepted_terms_and_conditions: true,
        });
        return { success: true };
      }
      await authApi.signUp({
        username: data.email,
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
      });
      return { success: true };
    } catch (error: unknown) {
      const parsed = error as ParsedApiError;
      if (parsed?.fieldErrors && Object.keys(parsed.fieldErrors).length > 0) {
        const fieldErrors: Record<string, string[]> = {};
        for (const [key, val] of Object.entries(parsed.fieldErrors)) {
          if (key === 'detail' || key === 'message') continue;
          fieldErrors[key] = [val];
        }
        if (Object.keys(fieldErrors).length > 0) {
          return {
            success: false,
            fieldErrors,
            error: Object.values(fieldErrors).flat().join(' '),
          };
        }
      }

      const errorMessage = handleApiError(error);
      if (errorMessage.includes('Network Error') || errorMessage.includes('timeout')) {
        return {
          success: false,
          error: 'Connection failed. Please check your internet connection and try again.',
        };
      }
      return {
        success: false,
        error: errorMessage || 'Registration failed. Please try again.',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      router.push('/login');
    }
  };

  const endSessionAfterPasswordChange = () => {
    beginIntentionalLogout();
    userRef.current = null;
    setUser(null);
    setAuthNotice(AUTH_NOTICE_PASSWORD_CHANGED);
    clearAuthData('password_changed');
    router.replace('/login');
  };

  const forgotPassword = async (email: string) => {
    setIsLoading(true);
    try {
      if (MOCK_MODE) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return { success: true };
      }
      await authApi.forgotPassword({ email });
      return { success: true };
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('Not Found')) {
          return {
            success: false,
            error: 'Email not found. Please check your email address and try again.',
          };
        }
        if (error.message.includes('400') || error.message.includes('Bad Request')) {
          return { success: false, error: 'Please enter a valid email address.' };
        }
        if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
          return {
            success: false,
            error: 'Too many reset requests. Please wait a few minutes before trying again.',
          };
        }
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          return {
            success: false,
            error: 'Connection failed. Please check your internet connection and try again.',
          };
        }
        if (error.message && error.message !== 'Something went wrong.') {
          return { success: false, error: error.message };
        }
      }
      return { success: false, error: 'Failed to request password reset. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string; field?: 'password' }> => {
    setIsLoading(true);
    try {
      if (MOCK_MODE) {
        beginIntentionalLogout();
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (currentPassword === 'password123') {
          endSessionAfterPasswordChange();
          return { success: true };
        }
        cancelIntentionalLogout();
        return { success: false, error: 'Current password is incorrect' };
      }
      // Identity comes from the session cookie — backend verifies current_password.
      beginIntentionalLogout();
      await authApi.changePassword({
        current_password: currentPassword,
        password: newPassword,
      });
      // Backend revokes refresh tokens and clears cookies; end the local session
      // before any follow-up 401 is treated as a generic expiry.
      endSessionAfterPasswordChange();
      return { success: true };
    } catch (error: unknown) {
      cancelIntentionalLogout();
      const parsed = error as ParsedApiError;
      const fieldErrors = parsed?.fieldErrors;

      if (fieldErrors?.current_password) {
        return { success: false, error: 'Current password is incorrect' };
      }

      if (fieldErrors?.password) {
        const passwordError = fieldErrors.password as string | string[];
        return {
          success: false,
          error: Array.isArray(passwordError) ? passwordError.join(' ') : passwordError,
          field: 'password',
        };
      }

      if (fieldErrors) {
        const errorMessages = Object.entries(fieldErrors)
          .filter(([key]) => key !== 'detail' && key !== 'message')
          .map(([, msg]) => msg)
          .filter(Boolean)
          .join(', ');
        if (errorMessages) {
          return { success: false, error: errorMessages };
        }
      }

      const message = parsed?.message ?? (error instanceof Error ? error.message : '');
      const status = parsed?.status;

      if (status === 400 || message.includes('400') || message.includes('Bad Request')) {
        return {
          success: false,
          error: 'Please check your password requirements and try again.',
        };
      }
      if (status === 401 || message.includes('401') || message.includes('Unauthorized')) {
        return { success: false, error: 'Your session has expired. Please log in again.' };
      }
      if (status === 403 || message.includes('403') || message.includes('Forbidden')) {
        return { success: false, error: 'You do not have permission to change your password.' };
      }
      if (message.includes('Network Error') || message.includes('timeout')) {
        return {
          success: false,
          error: 'Connection failed. Please check your internet connection and try again.',
        };
      }
      if (message && message !== 'Something went wrong.') {
        return { success: false, error: message };
      }

      return { success: false, error: 'Failed to change password. Please try again.' };
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const acceptTermsAndConditions = async (): Promise<{ success: boolean; error?: string }> => {
    if (!user?.id) {
      return { success: false, error: 'User not found' };
    }
    setIsLoading(true);
    try {
      if (MOCK_MODE) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setUser((prev) =>
          prev
            ? {
                ...prev,
                has_accepted_terms_and_conditions: true,
                datetime_last_accepted_terms_and_conditions: new Date().toISOString(),
              }
            : null
        );
        return { success: true };
      }
      await userApi.acceptTermsAndConditions(user.id);
      await fetchUser();
      setShowTermsModal(false);
      return { success: true };
    } catch (error: unknown) {
      console.error('AuthContext error - Full error object:', error);
      console.error(
        'AuthContext error message:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      console.error(
        'AuthContext error response:',
        (error as { response?: { data?: unknown } })?.response?.data
      );
      console.error(
        'AuthContext error status:',
        (error as { response?: { status?: number } })?.response?.status
      );

      if (error instanceof Error) {
        return { success: false, error: error.message };
      }
      return { success: false, error: 'Failed to accept terms and conditions' };
    } finally {
      setIsLoading(false);
    }
  };

  const closeTermsModal = () => {
    setShowTermsModal(false);
  };

  const checkAndShowTermsModal = () => {
    if (
      user &&
      (user.has_accepted_terms_and_conditions === false ||
        user.has_accepted_terms_and_conditions === undefined)
    ) {
      setShowTermsModal(true);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    showTermsModal,
    login,
    logout,
    register,
    refreshUser,
    forgotPassword,
    changePassword,
    endSessionAfterPasswordChange,
    acceptTermsAndConditions,
    closeTermsModal,
    checkAndShowTermsModal,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
